import { Dialog, DialogContent } from '@mui/material'
import { Button } from '../ui-primitives/button'
import React from 'react'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../ui-primitives/resizable'
import { Cross2Icon } from '@radix-ui/react-icons'
import { mergeClasses } from '../../helpers/styling'
import {
  MessageEntry,
  useEnableRuleTracking,
  useRuleExecutionLogs,
  useRuleTrackingIsEnabled,
} from './rule-execution-logs-store'
import { Switch } from '../ui-primitives/switch'
import { Label } from '../ui-primitives/label'
import { Tabs, TabsList, TabsTrigger } from '../ui-primitives/tabs'
import { TabsContent } from '@radix-ui/react-tabs'
import { Input } from '../ui-primitives/input'
import { Badge } from '../ui-primitives/badge'
import { Alert, AlertDescription } from '../ui-primitives/alert'

export function RuleExecutionLogsPopup(props: {
  data?: undefined
  open: boolean
  onClose: () => void
}) {
  const [selectedIdx, setSelectedIdx] = React.useState<number>(0)
  const logs = useRuleExecutionLogs()
  const trackingEnabled = useRuleTrackingIsEnabled()
  const setTrackingEnabled = useEnableRuleTracking()
  const [textFilter, setTextFilter] = React.useState<string>('')

  if (!logs.length) return (
    <DialogWrapper
      isOpen={props.open} close={props.onClose}
      isEnabled={trackingEnabled} enable={setTrackingEnabled}
    >
      <div className="text-center px-4 py-2 opacity-75">
        No rules have been executed by this app, since activating debug tracking.
      </div>
    </DialogWrapper>
  )

  const selected = logs[selectedIdx]
  if (!selected) {
    throw new Error('selected event-log-item not found')
  }

  return (
    <DialogWrapper
      isOpen={props.open} close={props.onClose}
      isEnabled={trackingEnabled} enable={setTrackingEnabled}
    >
      <ResizablePanelGroup orientation="horizontal" className="p-2">

        <ResizablePanel defaultSize={1} className="overflow-auto h-full flex flex-col px-4 py-2" style={{
          height: "calc(100vh - 200px)"
        }}>
          {logs.map((item, idx) => {

              return (
                <button key={item.id}
                  className={mergeClasses(
                    "flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-300 ease-in-out hover:scale-105",
                    { "bg-opacity-30": idx !== selectedIdx, "scale-105": idx === selectedIdx },
                    item.success ? "bg-green-100" : "bg-red-100",
                  )}
                  onClick={() => setSelectedIdx(idx)}
                >
                  <span>{item.success ? '✅' : '❌'}</span>
                  <span className="grow overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.tag}
                  </span>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>
                </button>
              )

        })}
        </ResizablePanel>

        <ResizableHandle withHandle className="mx-1" />

        <ResizablePanel defaultSize={2} className="overflow-auto h-full flex flex-col px-4 py-2 text-sm" style={{
          height: "calc(100vh - 200px)"
        }}>
              <div className="mb-3 flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">tag</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">
                    {selected.tag}
                  </code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>

              <div className="mb-3">
                <span className="opacity-75 mr-2">initial parameters</span>
                <pre className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">
                  {(() => {
                    try {
                      if (!selected.params) return ""
                      return JSON.stringify(JSON.parse(selected.params), null, 4) /* acceptable until it's not performant enough */
                    } catch(e) {
                      console.error("could not format params as JSON, giving out params as is", e)
                      return selected.params
                    }
                  })()}
                </pre>
              </div>

              <div className="mb-3">
                <Tabs defaultValue="parsed" className="flex flex-col gap-2">

                  <div className="mb-3 flex flex-row items-center">
                    <span className="opacity-75 mr-2">log statements</span>
                    <TabsList className="h-6">
                      <TabsTrigger value="parsed" className="h-6">parsed</TabsTrigger>
                      <TabsTrigger value="raw" className="h-6">raw</TabsTrigger>
                    </TabsList>
                  </div>

                  {selected.messages.some(m => m.level === 'WARN') && (
                    <Alert className="bg-yellow-200 text-black">
                      <AlertDescription>Rule engine reported one or more warnings.</AlertDescription>
                    </Alert>
                  )}

                  {selected.messages.some(m => m.level === 'ERROR') && (
                    <Alert className="bg-red-200 text-black">
                      <AlertDescription>Rule engine reported one or more errors.</AlertDescription>
                    </Alert>
                  )}

                  <TabsContent value="parsed" className="p-1 rounded-lg border flex flex-column gap-2">
                    <Input placeholder="text to filter for" value={textFilter} onChange={e => setTextFilter(e.target.value)} />
                      <div className="flex flex-col gap-1">
                      {selected.parsed
                      .filter(item => {
                        if (!textFilter.length) return true

                        return item.tag.includes(textFilter)
                        || item.uri.includes(textFilter)
                        || item.params.includes(textFilter)
                      })
                      .map(item => (
                        <div key={item.id}
                          className="relative rounded-md shadow-sm text-black flex flex-col text-left space-x-1 items-center font-mono text-sm border"
                        >
                          {(() => {
                            switch (item.type) {
                              case 'before-rule': return <Badge className="text-black bg-yellow-100 hover:bg-yellow-300 w-full rounded-b-none shadow-none">BEFORE</Badge>
                              case 'after-rule': return <Badge className="text-black bg-orange-100 hover:bg-orange-300 w-full rounded-b-none shadow-none">AFTER</Badge>
                              case 'rule-errors': return <Badge className="text-white bg-red-500 hover:bg-red-600 w-full rounded-b-none shadow-none">ERRORS</Badge>
                              default: throw new Error(`unknown item: ${JSON.stringify(item)}`)
                            }
                          })()}
                          <div className="grow flex flex-col px-2 py-1 w-full">
                            <div className="mb-3 flex flex-row items-center gap-2">
                              <span className="flex opacity-75">tag</span>
                              <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{item.tag}</code>
                              <span>
                                &lt;
                                <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{item.uri}</code>
                                &gt;
                              </span>
                            </div>
                            {item.errors && item.errors.length > 0 && (
                              <div className="mb-3 px-2">
                                <span className="flex opacity-75">errors</span>
                                {item.errors.map((err, i) => (
                                  <pre key={i} className="select-all text-red-900 bg-red-50 p-1 rounded-sm font-mono mb-1">{err}</pre>
                                ))}
                              </div>
                            )}
                            <div className="mb-3">
                              <span className="flex opacity-75">params</span>
                              <pre className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{item.params}</pre>
                            </div>
                          </div>
                          <span className="absolute right-0 top-0 opacity-75 text-xs select-all m-1">[{item.timestamp}]</span>
                        </div>
                      ))}
                      </div>
                  </TabsContent>

                  <TabsContent value="raw" className="p-1 rounded-lg border flex flex-column gap-2">
                    <Input placeholder="text to filter for" value={textFilter} onChange={e => setTextFilter(e.target.value)} />

                    <div>
                      {selected.messages
                      .filter (item =>
                        !textFilter.length
                          ? true
                          : item.message.includes(textFilter)
                      )
                      .map(item => (
                        <RawMessageEntry key={item.message + item.timestamp} entry={item} />
                      ))}
                    </div>
                  </TabsContent>

                </Tabs>
              </div>
        </ResizablePanel>

      </ResizablePanelGroup>
    </DialogWrapper>
  )
}

function RawMessageEntry(props: {
  entry: MessageEntry
}) {
  const hasMdc = props.entry.mdc && Object.keys(props.entry.mdc).length > 0
  return (
    <div key={props.entry.message + props.entry.timestamp} className={mergeClasses(
      'text-black flex flex-col text-left font-mono text-sm border-y w-100',
      {
        'bg-slate-200': props.entry.level === 'INFO',
        'bg-yellow-200': props.entry.level === 'WARN',
        'bg-red-200': props.entry.level === 'ERROR',
      },
    )}>
      <div className="flex flex-row flex-grow space-x-1 items-start w-100">
        <div className="flex flex-col grow p-1 gap-1 rounded-sm overflow-x-auto">
          {hasMdc && (
            <details className="flex flex-col text-blue-950">
              <summary>Context</summary>
              <table className="ml-[15px]">
                <tbody>
                {Object.entries(props.entry.mdc ?? {}).map(([key, value]) => {
                  return (
                    <tr>
                      <th className="text-bold">{key}</th>
                      <td className="select-all">{value}</td>
                    </tr>
                  )
                })}
                </tbody>
              </table>
            </details>
          )}
          {!!props.entry.stackTrace && (
            <details className="flex flex-col text-red-900">
              <summary>Stack trace</summary>
              <pre className="select-all text-red-900 ml-[15px]">{props.entry.stackTrace}</pre>
            </details>
          )}
          <pre className="grow select-all ml-[15px]">{props.entry.message.trim()}</pre>
          {!!props.entry.throwableMessage && (
            <pre className="grow select-all ml-[15px] text-red-900">{props.entry.throwableMessage.trim()}</pre>
          )}
        </div>

        <span className="opacity-75 text-xs select-none justify-items-start">[{props.entry.timestamp}]</span>
      </div>
    </div>
  )
}

function DialogWrapper(props: {
  children: React.ReactNode,
  isOpen: boolean,
  close: () => void,
  isEnabled: boolean,
  enable: (en: boolean) => void
}): React.ReactNode {
  return (
    <Dialog open={props.isOpen} fullWidth maxWidth="xl" onClose={props.close}>
      <DialogContent className="p-0 flex flex-col justify-center" style={{
        fontFamily: "'Exo 2 Variable', sans-serif",
      }}>
          <div className="relative text-center sm:text-left px-2 py-1 bg-wistor text-wistor-foreground">
            <span>Rule Execution Logs</span>
            <div className="absolute top-0 right-0 px-2 h-full flex flex-row items-center gap-4">
              <Label className="flex flex-row items-center space-x-2 w-40 cursor-pointer m-0">
                <Switch className="h-4 data-[state=checked]:bg-wistor-alt" checked={props.isEnabled} onCheckedChange={props.enable} />
                {props.isEnabled
                ? <span className="mb-0.5">tracking enabled</span>
                : <span className="mb-0.5 opacity-75">tracking disabled</span>
                }
              </Label>
              <Button variant="ghost" className="text-wistor-foreground p-0 h-4"
                onClick={props.close}
              >
                <Cross2Icon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </div>
        {props.children}
      </DialogContent>
    </Dialog>
  )
}
